package com.neurobs.ccabserver.administrator.view

import com.neurobs.ccab.{DataSyncState, DataSyncStateSource, DataSyncStateSourceSubscriber, SyncRequestStatus}
import com.neurobs.js.view.Container
import com.neurobs.js.dom.DOMViewElement._
import com.neurobs.react.StyleUtil
import scalatags.JsDom.all.{div => scalaTagsDiv}
import slinky.web.ReactDOM
import slinky.core.facade.Hooks.useState
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.core.facade.ReactElement
import slinky.web.html._

@react
object HorizontalSpace {
	case class Props(width: String)
	val component = FunctionalComponent[Props] { props =>
		div(style := StyleUtil.makeStyle(("width", props.width), ("display", "inline-block")))
	}
}

@react
object VerticalSpace {
	case class Props(height: String)
	val component = FunctionalComponent[Props] { props =>
		div(style := StyleUtil.makeStyle(("height", props.height)))
	}
}

@react
object VerticalScrollContainer {
	case class Props(maxHeight: String, content: ReactElement)
	val component = FunctionalComponent[Props] { props =>
		div(style := StyleUtil.makeStyle(("maxHeight", props.maxHeight), ("overflowY", "auto")), props.content)
	}
}

@react
object TestStationDataSyncList {
	case class Props(requests: Seq[SyncRequestStatus])

	val component = FunctionalComponent[Props] { props =>
		VerticalScrollContainer("350px",
			table(
				className := "ui compact celled table",
				style := StyleUtil.makeStyle(("maxHeight", "300px"), ("overflowY", "auto")),
				thead(tr(th("Type"), th("Dur(s)"), th("File"), th("error"))),
				tbody(
					props.requests map { r =>
						val rowClass = r.getStatus() match {
							case SyncRequestStatus.Succeeded => "positive"
							case SyncRequestStatus.Pending => ""
							case SyncRequestStatus.GeneralError => "negative"
							case SyncRequestStatus.FatalError => "negative"
						}
						tr( className := rowClass,
							key := r.file,
							td(r.`type`.toString),
							td(r.durs.map(_.toString).getOrElse(""): String),
							td(r.file),
							td(r.error.getOrElse(""): String)
						)
					}
				)
			)
		)
	}
}

@react
object TestStationDataSyncStatus {
	case class Props(stateSource: DataSyncStateSource)

	def makeLink(text: String, action: () => Unit) = a(
		className := "efactory-link",
		text,
		onClick := action
	)

	val component = FunctionalComponent[Props] { props =>
		val state = props.stateSource.getState()
		val statusLink = makeLink(
			if (state.paused) "(resume)" else "(pause)",
			() => props.stateSource.setPaused(!state.paused)
		)
		val statusText = if (state.paused) "paused" else "active"
		val backlogLink = makeLink(
			"(add backlog)",
			() => props.stateSource.addBacklog()
		)

		table(
			className := "ui compact very basic collapsing table",
			tbody(
				tr(
					td("Status:"), td(statusText, " ", statusLink)
				),
				tr(
					td("Pending:"), td(state.pending.toString, " ", if (state.backlogCount > 0) backlogLink else null)
				)
			)
		)
	}
}

@react
object TestStationDataSyncDetail {
	case class Props(stateSource: DataSyncStateSource)

	val component = FunctionalComponent[Props] { props =>
		val hideLink = a("hide", className := "efactory-link", onClick := {() => props.stateSource.setVisible(false)})
		val refreshLink = a("force refresh", className := "efactory-link", onClick := {() => props.stateSource.requestUpdate(reset = true)})

		div(
			className := "sync-detail-panel",
			div(style := StyleUtil.makeStyle(("textAlign", "center"), ("color", "black"), ("fontWeight", "bold")), "Test Station Sync State"),
			TestStationDataSyncStatus(props.stateSource),
			TestStationDataSyncList(props.stateSource.getState().requests),
			VerticalSpace("15px"),
			hideLink, HorizontalSpace("40px"), refreshLink
		)
	}
}

@react
object TestStationDataSyncHidden {
	case class Props(stateSource: DataSyncStateSource)

	val component = FunctionalComponent[Props] { props =>
		val detailsLink = a("details", className := "efactory-link", onClick := {() => props.stateSource.setVisible(true)})
		val state = props.stateSource.getState()
		val (statusClass, statusText) = {
			if (state.complete) ("sync-status-complete", "complete")
			else if (state.paused) ("sync-status-paused", "paused")
			else if (state.backlogCount > 0) ("sync-status-backlog", "pending backlog")
			else ("sync-status-uploading", "uploading...")
		}
		div(span(s"Sync status: $statusText", className := s"sync-status $statusClass"), span(" ("), detailsLink, span(")"))
	}
}

@react
object TestStationDataSyncComponent {

	case class Props(stateSource: DataSyncStateSource)

	val component = FunctionalComponent[Props] { props =>

		val (state, setState) = useState[DataSyncState](DataSyncState(Seq(), false, 0, false))

		val subscriber = new DataSyncStateSourceSubscriber {
			override def onNewState(state: DataSyncState): Unit = setState(state)
		}
		props.stateSource.setSubscriber(subscriber)

		if (state.visible) TestStationDataSyncDetail(props.stateSource)
		else TestStationDataSyncHidden(props.stateSource)
	}
}

class TestStationDataSyncView(stateSource: DataSyncStateSource) {
	val container: Container = {
		val main = scalaTagsDiv().render
		ReactDOM.render(
			TestStationDataSyncComponent(stateSource),
			main
		)
		main
	}
}
